// ============================================
// 불편사항 접수 컴포넌트
// 제목, 내용, 스크린샷 첨부 가능
// ============================================
import React, { useState } from 'react';
import { db, auth, storage, analytics, logEvent } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

function Feedback() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  // --------------------------------------------
  // 스크린샷 선택
  // --------------------------------------------
  const handleScreenshotChange = (e) => {
    const files = Array.from(e.target.files);

    // 최대 3개까지
    if (files.length + screenshots.length > 3) {
      setMessage('스크린샷은 최대 3개까지 첨부 가능합니다');
      return;
    }

    // 파일 크기 체크 (각 5MB 이하)
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setMessage('각 파일은 5MB 이하여야 합니다');
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      logEvent(analytics, 'add_feedback_screenshot', {
        count: validFiles.length
      });
    }

    setScreenshots([...screenshots, ...validFiles]);
    setMessage('');
  };

  // --------------------------------------------
  // 스크린샷 제거
  // --------------------------------------------
  const handleRemoveScreenshot = (index) => {
    const newScreenshots = screenshots.filter((_, i) => i !== index);
    setScreenshots(newScreenshots);
  };

  // --------------------------------------------
  // 불편사항 제출
  // --------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setMessage('제목을 입력해주세요');
      return;
    }

    if (!content.trim()) {
      setMessage('내용을 입력해주세요');
      return;
    }

    setUploading(true);
    setMessage('제출 중...');

    try {
      // 스크린샷 업로드
      const screenshotUrls = [];
      for (const screenshot of screenshots) {
        const screenshotRef = ref(
          storage, 
          `feedback/${auth.currentUser.uid}/${Date.now()}_${screenshot.name}`
        );
        await uploadBytes(screenshotRef, screenshot);
        const url = await getDownloadURL(screenshotRef);
        screenshotUrls.push(url);
      }

      // Firestore에 저장
      await addDoc(collection(db, 'feedback'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        title: title,
        content: content,
        screenshots: screenshotUrls,
        status: 'pending', // pending, in_progress, resolved
        createdAt: new Date(),
        updatedAt: new Date()
      });

      setMessage('불편사항이 접수되었습니다. 빠른 시일 내에 확인하겠습니다!');

      logEvent(analytics, 'submit_feedback', {
        has_screenshots: screenshotUrls.length > 0,
        screenshot_count: screenshotUrls.length
      });

      // 폼 초기화
      setTitle('');
      setContent('');
      setScreenshots([]);
      
      // 입력 필드 초기화
      const fileInput = document.getElementById('screenshot-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      setMessage('제출 실패: ' + error.message);
      console.error(error);
    }

    setUploading(false);
  };

  // --------------------------------------------
  // UI 렌더링
  // --------------------------------------------
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>불편사항 접수</h2>
        <p style={styles.subtitle}>
          앱 사용 중 불편한 점이나 개선사항을 알려주세요
        </p>
      </div>

      {message && (
        <div style={styles.message}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* 제목 */}
        <div style={styles.field}>
          <label style={styles.label}>제목 *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="예: 계단 기록 시 오류 발생"
            style={styles.input}
            maxLength={100}
            disabled={uploading}
          />
          <div style={styles.charCount}>
            {title.length} / 100
          </div>
        </div>

        {/* 내용 */}
        <div style={styles.field}>
          <label style={styles.label}>내용 *</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="자세한 내용을 입력해주세요&#10;&#10;예시:&#10;- 언제 발생했나요?&#10;- 어떤 상황에서 발생했나요?&#10;- 예상했던 동작은 무엇인가요?"
            style={styles.textarea}
            maxLength={1000}
            disabled={uploading}
          />
          <div style={styles.charCount}>
            {content.length} / 1000
          </div>
        </div>

        {/* 스크린샷 첨부 */}
        <div style={styles.field}>
          <label style={styles.label}>
            스크린샷 (선택, 최대 3개)
          </label>
          <input
            id="screenshot-input"
            type="file"
            accept="image/*"
            multiple
            onChange={handleScreenshotChange}
            style={styles.fileInput}
            disabled={uploading || screenshots.length >= 3}
          />
          <p style={styles.hint}>
            문제가 발생한 화면을 캡처해주시면 더 빠르게 해결할 수 있어요
          </p>

          {/* 첨부된 스크린샷 미리보기 */}
          {screenshots.length > 0 && (
            <div style={styles.screenshotList}>
              {screenshots.map((file, index) => (
                <div key={index} style={styles.screenshotItem}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`스크린샷 ${index + 1}`}
                    style={styles.screenshotPreview}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveScreenshot(index)}
                    style={styles.removeButton}
                    disabled={uploading}
                  >
                    ✕
                  </button>
                  <div style={styles.screenshotName}>
                    {file.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 제출 버튼 */}
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            ...(uploading ? styles.submitButtonDisabled : {})
          }}
          disabled={uploading}
        >
          {uploading ? '제출 중...' : '제출하기'}
        </button>
      </form>

      {/* 안내 사항 */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>안내사항</h3>
        <ul style={styles.infoList}>
          <li>접수된 내용은 관리자가 확인 후 처리됩니다</li>
          <li>답변이 필요한 경우 이메일로 연락드립니다</li>
          <li>개인정보가 포함된 스크린샷은 첨부하지 말아주세요</li>
          <li>욕설이나 비방은 삭제될 수 있습니다</li>
        </ul>
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================
const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px',
    fontSize: '14px',
    lineHeight: 1.4
  },
  header: {
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.5'
  },
  message: {
    padding: '10px',
    marginBottom: '12px',
    backgroundColor: '#eee',
    border: '1px solid #ddd',
    textAlign: 'center',
    fontSize: '13px'
  },
  messageSuccess: {
    backgroundColor: '#eee',
    color: '#333'
  },
  messageError: {
    backgroundColor: '#eee',
    color: '#333'
  },
  form: {
    backgroundColor: 'white',
    padding: '16px',
    border: '1px solid #ddd',
    marginBottom: '12px'
  },
  field: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600',
    color: '#333',
    fontSize: '13px'
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: '#fff'
  },
  textarea: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
    minHeight: '200px',
    resize: 'vertical',
    lineHeight: '1.6',
    outline: 'none',
    fontFamily: 'inherit',
    backgroundColor: '#fff'
  },
  charCount: {
    textAlign: 'right',
    fontSize: '11px',
    color: '#555',
    marginTop: '4px'
  },
  fileInput: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
    cursor: 'pointer',
    backgroundColor: '#fafafa'
  },
  hint: {
    fontSize: '11px',
    color: '#555',
    marginTop: '6px',
    lineHeight: '1.5'
  },
  screenshotList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px',
    marginTop: '12px'
  },
  screenshotItem: {
    position: 'relative',
    border: '1px solid #ddd',
    padding: '6px',
    backgroundColor: '#fafafa'
  },
  screenshotPreview: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    marginBottom: '6px'
  },
  removeButton: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '24px',
    height: '24px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  screenshotName: {
    fontSize: '11px',
    color: '#555',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  submitButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  submitButtonDisabled: {
    backgroundColor: '#bdbdbd',
    cursor: 'not-allowed'
  },
  infoBox: {
    backgroundColor: '#fafafa',
    padding: '12px',
    border: '1px solid #ddd'
  },
  infoTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px'
  },
  infoList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.6'
  }
};

export default Feedback;